import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Search, Filter, RefreshCw, X, AlertCircle } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import apiService from '../../services/apiService';
import Button from '../../components/ui/Button';
import PreciosRow from '../../components/precios-ray/PreciosRow';
import useDragToFill from '../../hooks/useDragToFill';
import './PreciosRayPage.css';

// Constants outside component (never recreated)
const CAMPOS_PRECIOS = [
  { key: 'precio_lista', label: 'Precio 1' },
  { key: 'precio1', label: 'Precio 2' },
  { key: 'precio2', label: 'Precio 3' },
  { key: 'precio3', label: 'Precio 4' },
  { key: 'precio4', label: 'Precio 5' },
  { key: 'precio5', label: 'Precio 6' },
  { key: 'precio6', label: 'Precio 7' }
];

const COLUMNAS_NAVEGACION = [
  'costo',
  'cantidad_descuento',
  'porcentaje_base',
  'precio_publico',
  ...CAMPOS_PRECIOS.filter(c => c.key !== 'precio6').flatMap(c => [`${c.key}_pct`, `${c.key}_precio`])
];

const normalizarTexto = (txt) => txt.toLowerCase().replace(/#\s*/g, '').replace(/\b0+(\d)/g, '$1');

const calcularPrecioConGanancia = (costo, porcentaje) => {
  if (!costo || costo <= 0 || !porcentaje) return 0;
  return Math.round((costo * (1 + porcentaje / 100)) * 100) / 100;
};

const calcularPorcentajeGanancia = (costo, precio) => {
  if (!costo || costo <= 0 || !precio || precio <= 0) return 0;
  return Math.round(((precio - costo) / costo) * 100 * 100) / 100;
};

const prepararQueryAPI = (query) => {
  if (!query) return query;
  const palabrasTexto = query.trim().split(/\s+/).filter(w => !/^\d+$/.test(w));
  if (palabrasTexto.length === 0) return query.trim();
  return palabrasTexto.join(' ');
};

const ordenarPorRelevancia = (productos, query) => {
  if (!query || query.trim().length < 2) return productos;
  const queryNorm = query.trim().toLowerCase();
  const queryWords = queryNorm.split(/\s+/);
  const normNum = (txt) => txt.toLowerCase().replace(/#\s*/g, '').replace(/\b0+(\d)/g, '$1');
  const queryNormNum = normNum(queryNorm);

  const scored = productos.map(p => {
    const nombre = (p.nombre || '').toLowerCase();
    const nombreNorm = normNum(nombre);
    let score = 0;
    if (nombreNorm.includes(queryNormNum)) score += 100;
    if (nombreNorm.startsWith(queryNormNum)) score += 50;
    if (queryWords.every(w => nombreNorm.includes(normNum(w)))) score += 30;
    score -= nombre.length * 0.1;
    return { ...p, _score: score };
  });

  scored.sort((a, b) => b._score - a._score);
  return scored;
};

const PreciosRayPage = () => {
  const { showToast, loading, setLoading } = useApp();

  // Filtros
  const [categorias, setCategorias] = useState([]);
  const [categoriaFiltro, setCategoriaFiltro] = useState('');
  const [busqueda, setBusqueda] = useState('');

  // Productos
  const [productos, setProductos] = useState([]);
  const [productosEditados, setProductosEditados] = useState({});
  const [porcentajes, setPorcentajes] = useState({});
  const [guardando, setGuardando] = useState(false);

  // Filtros de columna tipo Excel
  const [filtrosColumna, setFiltrosColumna] = useState({ codigo: '', nombre: '' });

  // Carga progresiva
  const [offset, setOffset] = useState(0);
  const [hayMas, setHayMas] = useState(true);
  const [cargandoMas, setCargandoMas] = useState(false);
  const [totalProductos, setTotalProductos] = useState(0);
  const limite = 50;

  const busquedaTrigger = useRef(0);
  const filtroNombreTimeout = useRef(null);
  const [buscandoNombre, setBuscandoNombre] = useState(false);

  // Refs para scroll sincronizado
  const tablaContainerRef = useRef(null);
  const topScrollRef = useRef(null);
  const tablaRef = useRef(null);

  // Refs for stable callback access (avoid stale closures)
  const productosRef = useRef(productos);
  productosRef.current = productos;
  const productosEditadosRef = useRef(productosEditados);
  productosEditadosRef.current = productosEditados;
  const porcentajesRef = useRef(porcentajes);
  porcentajesRef.current = porcentajes;

  // ---- Memoized filtered products ----
  const productosFiltrados = useMemo(() => {
    return productos.filter(producto => {
      if (filtrosColumna.codigo) {
        const codigoNorm = normalizarTexto(producto.codigo_corto || '');
        const filtroNorm = normalizarTexto(filtrosColumna.codigo);
        if (!codigoNorm.includes(filtroNorm)) return false;
      }
      if (filtrosColumna.nombre) {
        const nombreNorm = normalizarTexto(producto.nombre || '');
        const filtroWords = normalizarTexto(filtrosColumna.nombre).split(/\s+/);
        if (!filtroWords.every(w => nombreNorm.includes(w))) return false;
      }
      return true;
    });
  }, [productos, filtrosColumna.codigo, filtrosColumna.nombre]);

  const productosFiltradosRef = useRef(productosFiltrados);
  productosFiltradosRef.current = productosFiltrados;

  // ---- Effects ----
  useEffect(() => { cargarCategorias(); }, []);

  useEffect(() => {
    const tablaContainer = tablaContainerRef.current;
    const topScroll = topScrollRef.current;
    if (!tablaContainer || !topScroll) return;
    const syncFromTable = () => { topScroll.scrollLeft = tablaContainer.scrollLeft; };
    const syncFromTop = () => { tablaContainer.scrollLeft = topScroll.scrollLeft; };
    tablaContainer.addEventListener('scroll', syncFromTable);
    topScroll.addEventListener('scroll', syncFromTop);
    return () => {
      tablaContainer.removeEventListener('scroll', syncFromTable);
      topScroll.removeEventListener('scroll', syncFromTop);
    };
  }, []);

  useEffect(() => {
    if (tablaRef.current && topScrollRef.current) {
      const scrollContent = topScrollRef.current.querySelector('.top-scroll-content');
      if (scrollContent) {
        scrollContent.style.width = `${tablaRef.current.scrollWidth}px`;
      }
    }
  }, [productos]);

  useEffect(() => {
    setProductos([]);
    setOffset(0);
    setHayMas(true);
    cargarProductos(0, true);
  }, [categoriaFiltro]);

  useEffect(() => {
    if (filtroNombreTimeout.current) clearTimeout(filtroNombreTimeout.current);
    if (!filtrosColumna.nombre || filtrosColumna.nombre.length < 2) {
      if (filtrosColumna.nombre === '' && productos.length === 0) {
        setProductos([]);
        setOffset(0);
        setHayMas(true);
        cargarProductos(0, true);
      }
      return;
    }
    setBuscandoNombre(true);
    filtroNombreTimeout.current = setTimeout(() => { buscarPorNombre(filtrosColumna.nombre); }, 500);
    return () => { if (filtroNombreTimeout.current) clearTimeout(filtroNombreTimeout.current); };
  }, [filtrosColumna.nombre]);

  useEffect(() => {
    const container = tablaContainerRef.current;
    if (!container) return;
    const handleScroll = () => {
      if (cargandoMas || !hayMas || loading || buscandoNombre) return;
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollTop + clientHeight >= scrollHeight - 200) cargarMasProductos();
    };
    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [cargandoMas, hayMas, loading, offset, buscandoNombre, filtrosColumna.nombre]);

  // ---- API calls (not wrapped in useCallback - they're called from effects/handlers directly) ----
  const cargarCategorias = async () => {
    try {
      const result = await apiService.getCategorias();
      if (result.success) setCategorias(result.data || []);
    } catch (error) {
      console.error('Error cargando categorias:', error);
    }
  };

  const cargarProductos = async (nuevoOffset = 0, esReset = false) => {
    try {
      setLoading(true);
      const params = {
        pagina: Math.floor(nuevoOffset / limite) + 1,
        limite,
        categoria_id: categoriaFiltro || undefined,
        busqueda: busqueda ? prepararQueryAPI(busqueda) : undefined
      };
      const result = await apiService.getProductosParaRay(params);
      if (result.success) {
        const nuevos = result.data.productos || [];
        const total = result.data.paginacion?.total || 0;
        const ordenados = ordenarPorRelevancia(nuevos, busqueda);
        if (esReset) {
          setProductos(ordenados);
          setProductosEditados({});
          setPorcentajes({});
        } else {
          setProductos(prev => [...prev, ...ordenados]);
        }
        setTotalProductos(total);
        setOffset(nuevoOffset + nuevos.length);
        setHayMas(nuevoOffset + nuevos.length < total);
      }
    } catch (error) {
      console.error('Error cargando productos:', error);
      showToast('Error al cargar productos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const cargarMasProductos = async () => {
    if (cargandoMas || !hayMas || buscandoNombre) return;
    try {
      setCargandoMas(true);
      const terminoBusqueda = filtrosColumna.nombre || busqueda || undefined;
      const params = {
        pagina: Math.floor(offset / limite) + 1,
        limite,
        categoria_id: categoriaFiltro || undefined,
        busqueda: terminoBusqueda ? prepararQueryAPI(terminoBusqueda) : undefined
      };
      const result = await apiService.getProductosParaRay(params);
      if (result.success) {
        const nuevos = result.data.productos || [];
        const total = result.data.paginacion?.total || 0;
        const ordenados = ordenarPorRelevancia(nuevos, terminoBusqueda);
        setProductos(prev => [...prev, ...ordenados]);
        setOffset(prev => prev + nuevos.length);
        setHayMas(offset + nuevos.length < total);
      }
    } catch (error) {
      console.error('Error cargando mas productos:', error);
    } finally {
      setCargandoMas(false);
    }
  };

  const buscarProductos = () => {
    setProductos([]);
    setOffset(0);
    setHayMas(true);
    busquedaTrigger.current++;
    cargarProductos(0, true);
  };

  const buscarPorNombre = async (nombre) => {
    try {
      setBuscandoNombre(true);
      const params = {
        pagina: 1,
        limite: 200,
        categoria_id: categoriaFiltro || undefined,
        busqueda: prepararQueryAPI(nombre)
      };
      const result = await apiService.getProductosParaRay(params);
      if (result.success) {
        const nuevos = result.data.productos || [];
        const total = result.data.paginacion?.total || 0;
        const ordenados = ordenarPorRelevancia(nuevos, nombre);
        setProductos(ordenados);
        setTotalProductos(total);
        setOffset(nuevos.length);
        setHayMas(nuevos.length < total);
        setProductosEditados({});
        setPorcentajes({});
      }
    } catch (error) {
      console.error('Error buscando productos:', error);
      showToast('Error al buscar productos', 'error');
    } finally {
      setBuscandoNombre(false);
    }
  };

  // ---- Stable getters using refs ----
  const getCosto = useCallback((producto) => {
    const ed = productosEditadosRef.current[producto.id];
    if (ed?.costo_precios !== undefined) return ed.costo_precios;
    return producto.costo_precios || 0;
  }, []);

  const getValor = useCallback((producto, campo) => {
    const ed = productosEditadosRef.current[producto.id];
    if (ed?.[campo] !== undefined) return ed[campo];
    return producto[campo] || 0;
  }, []);

  const calcularPorcentajeBase = useCallback((producto) => {
    if (!producto.equivalencia || !producto.precio_base_lista || producto.precio_base_lista <= 0) return null;
    const precioLista = getValor(producto, 'precio_lista');
    if (!precioLista || precioLista <= 0) return null;
    const precioIndividuales = producto.precio_base_lista * producto.equivalencia;
    const porcentaje = ((precioIndividuales - precioLista) / precioLista) * 100;
    return Math.round(porcentaje * 100) / 100;
  }, [getValor]);

  const getPorcentajeBase = useCallback((producto) => {
    const ed = productosEditadosRef.current[producto.id];
    if (ed?.porcentaje_base !== undefined) return ed.porcentaje_base;
    const calculado = calcularPorcentajeBase(producto);
    return calculado !== null ? calculado : 0;
  }, [calcularPorcentajeBase]);

  const getPorcentaje = useCallback((productoId, campo) => {
    const pct = porcentajesRef.current[productoId];
    if (pct?.[campo] !== undefined) return pct[campo];
    const producto = productosRef.current.find(p => p.id === productoId);
    if (!producto) return '';
    const costo = getCosto(producto);
    const precio = getValor(producto, campo);
    if (costo > 0 && precio > 0) return calcularPorcentajeGanancia(costo, precio).toString();
    return '';
  }, [getCosto, getValor]);

  const getCantidadDescuento = useCallback((producto) => {
    const ed = productosEditadosRef.current[producto.id];
    if (ed?.cantidad_descuento !== undefined) return ed.cantidad_descuento;
    if (producto.producto_base_id && producto.cantidad_descuento_base !== undefined) return producto.cantidad_descuento_base;
    return producto.cantidad_descuento || 0;
  }, []);

  const calcularPorcentajeDescuento = useCallback((producto) => {
    const precioListaBase = producto.precio_base_lista;
    const precioLista = getValor(producto, 'precio_lista');
    const equivalencia = producto.equivalencia;
    if (!precioListaBase || precioListaBase <= 0 || !precioLista || precioLista <= 0 || !equivalencia || equivalencia <= 0) return 0;
    const precioListaPorUnidad = precioLista / equivalencia;
    return Math.round(((precioListaBase - precioListaPorUnidad) / precioListaBase) * 100 * 100) / 100;
  }, [getValor]);

  const getPorcentajeDescuento = useCallback((producto) => {
    const ed = productosEditadosRef.current[producto.id];
    if (ed?.porcentaje_descuento !== undefined) return ed.porcentaje_descuento;
    if (producto.producto_base_id && producto.precio_base_lista) return calcularPorcentajeDescuento(producto);
    return producto.porcentaje_descuento || 0;
  }, [calcularPorcentajeDescuento]);

  // ---- Stable handlers using useCallback ----
  const handleCostoChange = useCallback((productoId, valor) => {
    const valorNumerico = parseFloat(valor) || 0;
    const producto = productosRef.current.find(p => p.id === productoId);
    if (!producto) return;

    const porcentajeBaseActual = getPorcentajeBase(producto);
    const costoAnterior = getCosto(producto);
    const campos = ['precio_lista', 'precio1', 'precio2', 'precio3', 'precio4', 'precio5', 'precio6'];
    const nuevosPrecios = { costo_precios: valorNumerico };
    const nuevosPorcentajes = {};

    if (valorNumerico > 0) {
      campos.forEach(campo => {
        let porcentajeActual;
        const pctData = porcentajesRef.current[productoId];
        if (pctData?.[campo] !== undefined) {
          porcentajeActual = parseFloat(pctData[campo]);
        } else {
          const precioActual = getValor(producto, campo);
          porcentajeActual = (costoAnterior > 0 && precioActual > 0)
            ? calcularPorcentajeGanancia(costoAnterior, precioActual) : 0;
        }
        nuevosPorcentajes[campo] = porcentajeActual.toString();
        if (!isNaN(porcentajeActual) && porcentajeActual > 0) {
          nuevosPrecios[campo] = calcularPrecioConGanancia(valorNumerico, porcentajeActual);
        }
      });
    }

    nuevosPrecios.porcentaje_base = porcentajeBaseActual;

    setPorcentajes(prev => ({
      ...prev,
      [productoId]: { ...prev[productoId], ...nuevosPorcentajes }
    }));

    setProductosEditados(prev => ({
      ...prev,
      [productoId]: { ...prev[productoId], ...nuevosPrecios }
    }));
  }, [getCosto, getValor, getPorcentajeBase]);

  const handlePorcentajeChange = useCallback((productoId, campo, valor) => {
    const porcentaje = parseFloat(valor);

    setPorcentajes(prev => ({
      ...prev,
      [productoId]: { ...prev[productoId], [campo]: valor }
    }));

    if (!isNaN(porcentaje)) {
      const producto = productosRef.current.find(p => p.id === productoId);
      const costo = getCosto(producto);
      if (costo > 0) {
        const nuevoPrecio = calcularPrecioConGanancia(costo, porcentaje);
        let porcentajeBaseActual;
        if (campo === 'precio_lista') porcentajeBaseActual = getPorcentajeBase(producto);

        setProductosEditados(prev => {
          const updated = {
            ...prev,
            [productoId]: { ...prev[productoId], [campo]: nuevoPrecio }
          };
          if (campo === 'precio_lista') updated[productoId].porcentaje_base = porcentajeBaseActual;
          if ((campo === 'precio_lista' || campo === 'precio1') && updated[productoId]?.porcentaje_descuento !== undefined) {
            delete updated[productoId].porcentaje_descuento;
          }
          return updated;
        });
      }
    }
  }, [getCosto, getPorcentajeBase]);

  const handlePrecioChange = useCallback((productoId, campo, valor) => {
    const valorNumerico = parseFloat(valor) || 0;

    let porcentajeBaseActual;
    if (campo === 'precio_lista') {
      const producto = productosRef.current.find(p => p.id === productoId);
      porcentajeBaseActual = getPorcentajeBase(producto);
    }

    setProductosEditados(prev => {
      const updated = {
        ...prev,
        [productoId]: { ...prev[productoId], [campo]: valorNumerico }
      };
      if (campo === 'precio_lista') updated[productoId].porcentaje_base = porcentajeBaseActual;
      if ((campo === 'precio_lista' || campo === 'precio1') && updated[productoId]?.porcentaje_descuento !== undefined) {
        delete updated[productoId].porcentaje_descuento;
      }
      return updated;
    });

    const producto = productosRef.current.find(p => p.id === productoId);
    const costo = getCosto(producto);
    if (costo > 0 && valorNumerico > 0) {
      const pct = calcularPorcentajeGanancia(costo, valorNumerico);
      setPorcentajes(prev => ({
        ...prev,
        [productoId]: { ...prev[productoId], [campo]: pct.toString() }
      }));
    }
  }, [getCosto, getPorcentajeBase]);

  const handleDescuentoChange = useCallback((productoId, valor) => {
    const valorNumerico = parseFloat(valor) || 0;
    setProductosEditados(prev => ({
      ...prev,
      [productoId]: { ...prev[productoId], cantidad_descuento: valorNumerico }
    }));
  }, []);

  const handlePorcentajeBaseChange = useCallback((productoId, valor) => {
    const porcentaje = parseFloat(valor);
    const porcentajeBase = isNaN(porcentaje) ? 0 : porcentaje;
    setProductosEditados(prev => ({
      ...prev,
      [productoId]: { ...prev[productoId], porcentaje_base: porcentajeBase }
    }));
  }, []);

  const guardarProducto = useCallback(async (productoId) => {
    const cambios = productosEditadosRef.current[productoId];
    if (!cambios) return;

    try {
      setGuardando(true);
      const preciosCambios = {};
      let cantidadDescuento = null;
      let porcentajeDescuento = null;
      let porcentajeBase = null;

      Object.keys(cambios).forEach(key => {
        if (key === 'cantidad_descuento') cantidadDescuento = cambios[key];
        else if (key === 'porcentaje_descuento') porcentajeDescuento = cambios[key];
        else if (key === 'porcentaje_base') porcentajeBase = cambios[key];
        else preciosCambios[key] = cambios[key];
      });

      if (Object.keys(preciosCambios).length > 0 || porcentajeBase !== null) {
        const datosPrecios = { ...preciosCambios };
        if (porcentajeBase !== null) {
          datosPrecios.porcentaje_base = porcentajeBase;
          if (datosPrecios.precio_lista === undefined) {
            const producto = productosRef.current.find(p => p.id === productoId);
            datosPrecios.precio_lista = producto?.precio_lista || 0;
          }
        }
        const result = await apiService.actualizarPreciosRay(productoId, datosPrecios);
        if (!result.success) {
          showToast(result.message || 'Error al actualizar precios', 'error');
          return;
        }
      }

      const producto = productosRef.current.find(p => p.id === productoId);
      const tieneEquivalencia = producto?.equivalencia && producto?.equivalencia > 0;
      const preciosCambiaron = Object.keys(preciosCambios).length > 0;
      const descuentoEditado = cantidadDescuento !== null || porcentajeDescuento !== null;
      const autoGuardarDesc = tieneEquivalencia && preciosCambiaron;

      if (descuentoEditado || autoGuardarDesc) {
        const idParaDescuento = producto?.producto_base_id || productoId;
        const cantidadFinal = cantidadDescuento !== null ? cantidadDescuento : getCantidadDescuento(producto);

        if (cantidadFinal > 0 || descuentoEditado) {
          let porcentajeFinal;
          if (porcentajeDescuento !== null) {
            porcentajeFinal = porcentajeDescuento;
          } else if (tieneEquivalencia) {
            const precioListaFinal = preciosCambios.precio_lista ?? producto.precio_lista;
            const pctBase = porcentajeBase !== null ? porcentajeBase : 0;
            let precioBaseListaNuevo = producto.precio_base_lista;
            if (pctBase !== 0) {
              precioBaseListaNuevo = (precioListaFinal / producto.equivalencia) * (1 + pctBase / 100);
            } else if (preciosCambios.precio_lista !== undefined) {
              precioBaseListaNuevo = precioListaFinal / producto.equivalencia;
            }
            const precioListaPorUnidad = precioListaFinal / producto.equivalencia;
            if (precioBaseListaNuevo > 0) {
              porcentajeFinal = Math.round(((precioBaseListaNuevo - precioListaPorUnidad) / precioBaseListaNuevo) * 100 * 100) / 100;
            } else {
              porcentajeFinal = 0;
            }
          } else {
            porcentajeFinal = getPorcentajeDescuento(producto);
          }
          await apiService.updateDescuentoVolumenRay(idParaDescuento, cantidadFinal, porcentajeFinal);
        }
      }

      setProductosEditados(prev => {
        const nuevo = { ...prev };
        delete nuevo[productoId];
        return nuevo;
      });

      setPorcentajes(prev => {
        const nuevo = { ...prev };
        delete nuevo[productoId];
        return nuevo;
      });

      setProductos(prev => prev.map(p => {
        if (p.id === productoId) {
          const tieneBase = producto?.producto_base_id;
          const equiv = p.equivalencia;
          let nuevoPrecioBaseLista = p.precio_base_lista;
          let nuevoPrecioBasePrecio1 = p.precio_base_precio1;
          if (equiv && equiv > 0) {
            const incremento = (porcentajeBase !== null && porcentajeBase !== 0) ? (1 + porcentajeBase / 100) : 1;
            const precioListaEnviado = preciosCambios.precio_lista !== undefined || porcentajeBase !== null;
            if (precioListaEnviado) {
              const precioListaFinal = preciosCambios.precio_lista ?? p.precio_lista;
              nuevoPrecioBaseLista = Math.round((precioListaFinal / equiv) * incremento * 1000) / 1000;
            }
            if (preciosCambios.precio1 !== undefined) {
              nuevoPrecioBasePrecio1 = Math.round((preciosCambios.precio1 / equiv) * 1000) / 1000;
            }
          }
          return {
            ...p,
            ...preciosCambios,
            precio_base_lista: nuevoPrecioBaseLista,
            precio_base_precio1: nuevoPrecioBasePrecio1,
            cantidad_descuento_base: tieneBase ? (cantidadDescuento ?? p.cantidad_descuento_base) : p.cantidad_descuento_base,
            porcentaje_descuento_base: tieneBase ? (porcentajeDescuento ?? p.porcentaje_descuento_base) : p.porcentaje_descuento_base,
            cantidad_descuento: !tieneBase ? (cantidadDescuento ?? p.cantidad_descuento) : p.cantidad_descuento,
            porcentaje_descuento: !tieneBase ? (porcentajeDescuento ?? p.porcentaje_descuento) : p.porcentaje_descuento
          };
        }
        return p;
      }));

      showToast('Producto actualizado', 'success');
    } catch (error) {
      console.error('Error guardando:', error);
      showToast('Error al guardar cambios', 'error');
    } finally {
      setGuardando(false);
    }
  }, [showToast, getCantidadDescuento, getPorcentajeDescuento]);

  const cancelarCambios = useCallback((productoId) => {
    setProductosEditados(prev => {
      const nuevo = { ...prev };
      delete nuevo[productoId];
      return nuevo;
    });
    setPorcentajes(prev => {
      const nuevo = { ...prev };
      delete nuevo[productoId];
      return nuevo;
    });
  }, []);

  const guardarTodos = useCallback(async () => {
    const productosConCambios = Object.keys(productosEditadosRef.current);
    if (productosConCambios.length === 0) {
      showToast('No hay cambios para guardar', 'info');
      return;
    }
    setGuardando(true);
    let guardados = 0, errores = 0;
    for (const productoId of productosConCambios) {
      try {
        await guardarProducto(parseInt(productoId));
        guardados++;
      } catch (error) { errores++; }
    }
    setGuardando(false);
    if (errores === 0) showToast(`${guardados} productos actualizados`, 'success');
    else showToast(`${guardados} guardados, ${errores} errores`, 'warning');
  }, [showToast, guardarProducto]);

  const cancelarTodos = useCallback(() => {
    setProductosEditados({});
    setPorcentajes({});
  }, []);

  const handleKeyNavigation = useCallback((e, rowIndex, colName) => {
    const currentColIndex = COLUMNAS_NAVEGACION.indexOf(colName);
    let targetRow = rowIndex;
    let targetCol = currentColIndex;
    const maxRow = productosFiltradosRef.current.length - 1;

    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        targetRow = Math.max(0, rowIndex - 1);
        break;
      case 'ArrowDown':
        e.preventDefault();
        targetRow = Math.min(maxRow, rowIndex + 1);
        break;
      case 'ArrowLeft':
        e.preventDefault();
        targetCol = Math.max(0, currentColIndex - 1);
        break;
      case 'ArrowRight':
        e.preventDefault();
        targetCol = Math.min(COLUMNAS_NAVEGACION.length - 1, currentColIndex + 1);
        break;
      case 'Tab':
        e.preventDefault();
        if (e.shiftKey) {
          if (currentColIndex > 0) targetCol = currentColIndex - 1;
          else if (rowIndex > 0) { targetRow = rowIndex - 1; targetCol = COLUMNAS_NAVEGACION.length - 1; }
        } else {
          if (currentColIndex < COLUMNAS_NAVEGACION.length - 1) targetCol = currentColIndex + 1;
          else if (rowIndex < maxRow) { targetRow = rowIndex + 1; targetCol = 0; }
        }
        break;
      case 'Enter':
        e.preventDefault();
        targetRow = Math.min(maxRow, rowIndex + 1);
        break;
      default:
        return;
    }

    const targetColName = COLUMNAS_NAVEGACION[targetCol];
    const targetInput = document.querySelector(`input[data-row="${targetRow}"][data-col="${targetColName}"]`);
    if (targetInput) { targetInput.focus(); targetInput.select(); }
  }, []);

  // ---- Drag-to-fill ----
  const batchApplyChanges = useCallback((editedMap, porcentajesMap) => {
    if (Object.keys(editedMap).length > 0) {
      setProductosEditados(prev => {
        const next = { ...prev };
        for (const [id, changes] of Object.entries(editedMap)) {
          next[id] = { ...(next[id] || {}), ...changes };
        }
        return next;
      });
    }
    if (Object.keys(porcentajesMap).length > 0) {
      setPorcentajes(prev => {
        const next = { ...prev };
        for (const [id, changes] of Object.entries(porcentajesMap)) {
          next[id] = { ...(next[id] || {}), ...changes };
        }
        return next;
      });
    }
  }, []);

  const { dragState, handleDragHandleMouseDown, isRowInDragRange } = useDragToFill({
    productosRef: productosFiltradosRef,
    getValorFromRef: getValor,
    getCostoFromRef: getCosto,
    getPorcentajeFromRef: getPorcentaje,
    getPorcentajeBaseFromRef: getPorcentajeBase,
    calcularPrecioConGanancia,
    calcularPorcentajeGanancia,
    batchApplyChanges
  });

  // ---- Derived state ----
  const cantidadEditados = Object.keys(productosEditados).length;
  const hayFiltrosActivos = filtrosColumna.codigo !== '' || filtrosColumna.nombre !== '';

  const handleFiltroColumna = useCallback((campo, valor) => {
    setFiltrosColumna(prev => ({ ...prev, [campo]: valor }));
  }, []);

  const limpiarFiltrosColumna = useCallback(() => {
    const teniaFiltroNombre = filtrosColumna.nombre !== '';
    setFiltrosColumna({ codigo: '', nombre: '' });
    if (teniaFiltroNombre) {
      setProductos([]);
      setOffset(0);
      setHayMas(true);
      setTimeout(() => cargarProductos(0, true), 100);
    }
  }, [filtrosColumna.nombre]);

  const handleLimpiarTodo = useCallback(() => {
    setBusqueda('');
    setCategoriaFiltro('');
    setFiltrosColumna({ codigo: '', nombre: '' });
    setProductos([]);
    setOffset(0);
    setHayMas(true);
    cargarProductos(0, true);
  }, []);

  // ---- Render ----
  return (
    <div className="precios-ray-page">
      {/* Filtros */}
      <div className="filtros-container">
        <div className="filtro-grupo">
          <label><Filter size={14} /> Categoria</label>
          <select value={categoriaFiltro} onChange={(e) => setCategoriaFiltro(e.target.value)}>
            <option value="">Todas las categorias</option>
            {categorias.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.nombre}</option>
            ))}
          </select>
        </div>

        <div className="filtro-grupo busqueda">
          <label><Search size={14} /> Buscar</label>
          <div className="busqueda-input">
            <input
              type="text"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && buscarProductos()}
              placeholder="Codigo o descripcion..."
            />
            <Button variant="primary" onClick={buscarProductos} disabled={loading}>
              <Search size={16} />
            </Button>
          </div>
        </div>

        <div className="filtro-grupo">
          <label>&nbsp;</label>
          <Button variant="secondary" onClick={handleLimpiarTodo}>
            <RefreshCw size={16} />
            Limpiar
          </Button>
        </div>

        {cantidadEditados > 0 && (
          <div className="filtro-grupo acciones-pendientes">
            <label>&nbsp;</label>
            <div className="acciones-btns">
              <span className="cambios-pendientes">
                <AlertCircle size={14} />
                {cantidadEditados}
              </span>
              <Button variant="secondary" size="sm" onClick={cancelarTodos} disabled={guardando}>
                Cancelar
              </Button>
              <Button variant="success" size="sm" onClick={guardarTodos} disabled={guardando}>
                {guardando ? <RefreshCw size={14} className="spin" /> : null}
                Guardar
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="info-bar">
        <span>
          {buscandoNombre ? (
            <>
              <RefreshCw size={12} className="spin" style={{ marginRight: '4px' }} />
              Buscando "{filtrosColumna.nombre}"...
            </>
          ) : (
            <>
              Mostrando {productosFiltrados.length} de {productos.length} productos cargados (Total: {totalProductos})
              {filtrosColumna.nombre && <span className="filtro-activo-badge">Filtrado por: "{filtrosColumna.nombre}"</span>}
            </>
          )}
          {hayFiltrosActivos && (
            <button className="btn-limpiar-filtros" onClick={limpiarFiltrosColumna} title="Limpiar filtros">
              <X size={12} /> Limpiar filtros
            </button>
          )}
        </span>
        {hayMas && !buscandoNombre && <span className="mas-productos">Desplaza para cargar mas...</span>}
      </div>

      {/* Scrollbar superior */}
      <div className="top-scroll-wrapper" ref={topScrollRef}>
        <div className="top-scroll-content"></div>
      </div>

      {/* Tabla de precios */}
      <div className="tabla-precios-container" ref={tablaContainerRef}>
        <table className="tabla-precios" ref={tablaRef}>
          <thead>
            <tr>
              <th className="col-codigo">Codigo</th>
              <th className="col-descripcion">Descripcion</th>
              <th className="col-costo">Costo</th>
              <th className="col-cantidad">Cant.Desc</th>
              <th className="col-porcentaje-desc" style={{ display: 'none' }}>%Desc</th>
              <th className="col-porcentaje-base">%Incre</th>
              <th className="col-precio-publico">P.Publico</th>
              {CAMPOS_PRECIOS.map(campo => (
                <th key={campo.key} className="col-precio-doble" style={campo.key === 'precio6' ? { display: 'none' } : undefined}>
                  <div className="header-precio">
                    <span>{campo.label}</span>
                    <small>% / $</small>
                  </div>
                </th>
              ))}
              <th className="col-acciones">Acc.</th>
            </tr>
            {/* Fila de filtros tipo Excel */}
            <tr className="fila-filtros">
              <th className="col-codigo">
                <input
                  type="text"
                  value={filtrosColumna.codigo}
                  onChange={(e) => handleFiltroColumna('codigo', e.target.value)}
                  placeholder="Filtrar..."
                  className="filtro-input"
                />
              </th>
              <th className="col-descripcion">
                <input
                  type="text"
                  value={filtrosColumna.nombre}
                  onChange={(e) => handleFiltroColumna('nombre', e.target.value)}
                  placeholder="Filtrar..."
                  className="filtro-input"
                />
              </th>
              <th className="col-costo"></th>
              <th className="col-cantidad"></th>
              <th className="col-porcentaje-desc" style={{ display: 'none' }}></th>
              <th className="col-porcentaje-base"></th>
              <th className="col-precio-publico"></th>
              {CAMPOS_PRECIOS.map(campo => (
                <th key={`filtro-${campo.key}`} className="col-precio-doble" style={campo.key === 'precio6' ? { display: 'none' } : undefined}></th>
              ))}
              <th className="col-acciones">
                {hayFiltrosActivos && (
                  <button className="btn-accion cancelar" onClick={limpiarFiltrosColumna} title="Limpiar filtros">
                    <X size={12} />
                  </button>
                )}
              </th>
            </tr>
          </thead>
          <tbody>
            {productosFiltrados.map((producto, rowIndex) => (
              <PreciosRow
                key={producto.id}
                producto={producto}
                rowIndex={rowIndex}
                editedData={productosEditados[producto.id]}
                porcentajesData={porcentajes[producto.id]}
                isEdited={productosEditados[producto.id] !== undefined}
                getCosto={getCosto}
                getValor={getValor}
                getPorcentaje={getPorcentaje}
                getCantidadDescuento={getCantidadDescuento}
                getPorcentajeBase={getPorcentajeBase}
                handleCostoChange={handleCostoChange}
                handleDescuentoChange={handleDescuentoChange}
                handlePorcentajeBaseChange={handlePorcentajeBaseChange}
                handlePorcentajeChange={handlePorcentajeChange}
                handlePrecioChange={handlePrecioChange}
                handleKeyNavigation={handleKeyNavigation}
                guardarProducto={guardarProducto}
                cancelarCambios={cancelarCambios}
                guardando={guardando}
                onDragHandleMouseDown={handleDragHandleMouseDown}
                isDragHighlighted={isRowInDragRange(rowIndex)}
                dragOriginRow={dragState?.originRow ?? -1}
              />
            ))}
          </tbody>
        </table>

        {productosFiltrados.length === 0 && !loading && (
          <div className="sin-resultados">
            <AlertCircle size={48} />
            <p>{hayFiltrosActivos ? 'No hay productos que coincidan con los filtros' : 'No se encontraron productos'}</p>
            {hayFiltrosActivos && (
              <Button variant="secondary" onClick={limpiarFiltrosColumna}>
                Limpiar filtros
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Indicador de carga progresiva */}
      {cargandoMas && (
        <div className="cargando-mas">
          <RefreshCw size={16} className="spin" />
          <span>Cargando mas productos...</span>
        </div>
      )}

      {!hayMas && productos.length > 0 && (
        <div className="fin-lista">
          <span>— Fin de la lista ({totalProductos} productos) —</span>
        </div>
      )}
    </div>
  );
};

export default PreciosRayPage;
